import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { AttendanceZoneOption } from '@shared/utils/attendance-config.util';

interface WorkCenterZonePickerDialogData {
  zones: AttendanceZoneOption[];
  selectedZoneIds: string[];
}

@Component({
  selector: 'app-work-center-zone-picker-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    TranslateModule,
  ],
  templateUrl: './work-center-zone-picker-dialog.component.html',
  styleUrl: './work-center-zone-picker-dialog.component.scss',
})
export class WorkCenterZonePickerDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<WorkCenterZonePickerDialogComponent, string[] | undefined>);
  readonly data = inject<WorkCenterZonePickerDialogData>(MAT_DIALOG_DATA);

  readonly searchTerm = signal('');
  readonly selectedZoneIds = signal<Set<string>>(new Set(this.data.selectedZoneIds || []));

  readonly filteredZones = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const zones = [...this.data.zones].sort((left, right) => left.name.localeCompare(right.name));
    if (!term) {
      return zones;
    }

    return zones.filter((zone) => zone.name.toLowerCase().includes(term));
  });

  updateSearch(value: string): void {
    this.searchTerm.set(value);
  }

  toggleZone(zoneId: string): void {
    const next = new Set(this.selectedZoneIds());
    if (next.has(zoneId)) {
      next.delete(zoneId);
    } else {
      next.add(zoneId);
    }
    this.selectedZoneIds.set(next);
  }

  isSelected(zoneId: string): boolean {
    return this.selectedZoneIds().has(zoneId);
  }

  close(): void {
    this.dialogRef.close(undefined);
  }

  confirm(): void {
    this.dialogRef.close([...this.selectedZoneIds()]);
  }
}
